import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { ArrowRight, ExternalLink } from 'lucide-react';

const CTASection = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <section 
      id="cta" 
      ref={ref}
      className="section-spacing relative section-dark overflow-hidden"
      data-testid="cta-section"
    >
      {/* Decorative Elements */}
      <div className="absolute top-1/4 left-0 w-72 h-72 bg-[#2563EB]/20 rounded-full blur-[100px]" />
      <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-[#2563EB]/10 rounded-full blur-[100px]" />
      
      <div className="section-container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto text-center"
        >
          {/* Headline */}
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4">
            <span className="text-white">{t.cta.headline}</span>
          </h2>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-8" style={{
            background: 'linear-gradient(135deg, #60A5FA 0%, #2563EB 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            {t.cta.subheadline}
          </h2>
          
          {/* Description */}
          <p className="text-lg text-[#94A3B8] max-w-2xl mx-auto mb-12 leading-relaxed">
            {t.cta.description}
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.a
              href="https://dashboard.aqera.app"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary group text-lg px-10 py-5"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="cta-primary"
            >
              {t.cta.button}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </motion.a>
            
            <motion.a
              href="https://dashboard.aqera.app"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/10 text-white px-10 py-5 rounded-full font-medium text-lg border border-white/20 hover:bg-white/20 transition-all inline-flex items-center justify-center gap-2 group"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="cta-dashboard"
            >
              {t.cta.dashboard}
              <ExternalLink className="w-4 h-4 opacity-50 group-hover:opacity-100 transition-opacity" />
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTASection;
